import { useCallback, useMemo } from "react";
import { isNil } from "lodash";
import { MONTHS_IN_YEAR, WEEKS_IN_MONTH_FROM, WEEKS_IN_MONTH_TO, WEEKS_IN_YEAR } from "shared/constants/sla-limits";

export const useLimitsRules = (warningCallback, warningCleanup) => {
    const handleTotalValidation = useCallback((fieldName, value, physical, digital) => {
        if (!physical && !digital) {
            return true;
        }

        const parsedValue = value ? Number(value) : 0;
        const physicalValue = physical ? Number(physical) : 0;
        const digitalValue = digital ? Number(digital) : 0;

        const total = physicalValue + digitalValue;

        if (total === parsedValue) {
            return true;
        }

        return "The total amount must equal the sum of Physical and Digital items";
    }, []);

    const handleMonthlyLimitValidation = useCallback(
        (value, limit, fieldName) => {
            if (!value) {
                warningCleanup(fieldName);

                return true;
            }

            const parsedValue = value ? Number(value) : 0;
            const totalFrom = Math.floor(limit * WEEKS_IN_MONTH_FROM);
            const totalTo = Math.floor(limit * WEEKS_IN_MONTH_TO);

            if (limit && parsedValue >= totalFrom && parsedValue <= totalTo) {
                warningCleanup(fieldName);
            } else {
                warningCallback(fieldName, "Weekly, Monthly and Annual product limit doesn’t add up correctly, we will proceed with lower limit");
            }
        },
        [warningCallback, warningCleanup]
    );

    const handleAnnualLimitValidation = useCallback(
        (value, weeklyLimit, monthlyLimit, annualLimit, fieldName) => {
            if (!value) {
                warningCleanup(fieldName);

                return true;
            }

            const parsedValue = value ? Number(value) : 0;
            const parsedMonthlyLimit = monthlyLimit ? Number(monthlyLimit) : 0;
            const parsedWeeklyLimit = weeklyLimit ? Number(weeklyLimit) : 0;
            const totalAnnuallyWeeksInYear = Math.floor(parsedWeeklyLimit * WEEKS_IN_YEAR);
            const totalAnnuallyMonthsInYear = Math.floor(parsedMonthlyLimit * MONTHS_IN_YEAR);

            if (weeklyLimit && annualLimit && totalAnnuallyWeeksInYear !== parsedValue) {
                warningCallback(fieldName, "Weekly, Monthly and Annual product limit doesn’t add up correctly, we will proceed with lower limit");

                return true;
            }

            if (monthlyLimit && annualLimit && !weeklyLimit && totalAnnuallyMonthsInYear !== parsedValue) {
                warningCallback(fieldName, "Weekly, Monthly and Annual product limit doesn’t add up correctly, we will proceed with lower limit");

                return true;
            }

            warningCleanup(fieldName);
        },
        [warningCallback, warningCleanup]
    );

    return useMemo(() => {
        return [
            [
                {
                    name: "rowDescription",
                    label: "Total:",
                },
                {
                    name: "weeklyLimit",
                    rules: {
                        min: {
                            value: 1,
                            message: "Limit must be greater or equal to 1",
                        },
                        validate: {
                            required: (value, formFields) => {
                                if (!value && !formFields.monthlyLimit && !formFields.annualLimit) {
                                    return "At least 1 limit must be defined. Please enter a valid value to continue";
                                }

                                return handleTotalValidation(
                                    "weeklyLimit",
                                    formFields.weeklyLimit,
                                    formFields.physicalWeeklyLimit,
                                    formFields.digitalWeeklyLimit
                                );
                            },
                        },
                    },
                },
                {
                    name: "monthlyLimit",
                    rules: {
                        min: {
                            value: 1,
                            message: "Limit must be greater or equal to 1",
                        },
                        validate: {
                            required: (value, formFields) => {
                                if (!value && !formFields.weeklyLimit && !formFields.annualLimit) {
                                    return "At least 1 limit must be defined. Please enter a valid value to continue";
                                }

                                if (value && formFields.weeklyLimit) {
                                    handleMonthlyLimitValidation(value, formFields.weeklyLimit, "monthlyLimit");
                                } else {
                                    warningCleanup("monthlyLimit");
                                }

                                return handleTotalValidation(
                                    "monthlyLimit",
                                    formFields.monthlyLimit,
                                    formFields.physicalMonthlyLimit,
                                    formFields.digitalMonthlyLimit
                                );
                            },
                        },
                    },
                },
                {
                    name: "annualLimit",
                    rules: {
                        min: {
                            value: 1,
                            message: "Limit must be greater or equal to 1",
                        },
                        validate: {
                            required: (value, formFields) => {
                                if (!value && !formFields.weeklyLimit && !formFields.monthlyLimit) {
                                    return "At least 1 limit must be defined. Please enter a valid value to continue";
                                }

                                handleAnnualLimitValidation(
                                    value,
                                    formFields.weeklyLimit,
                                    formFields.monthlyLimit,
                                    formFields.annualLimit,
                                    "annualLimit"
                                );

                                return handleTotalValidation(
                                    "annualLimit",
                                    formFields.annualLimit,
                                    formFields.physicalAnnualLimit,
                                    formFields.digitalAnnualLimit
                                );
                            },
                        },
                    },
                },
            ],
            [
                {
                    name: "rowDescription",
                    label: "Physical:",
                },
                {
                    name: "physicalWeeklyLimit",
                    rules: {
                        min: {
                            value: 0,
                            message: "Limit must be greater or equal to 0",
                        },
                        validate: {
                            required: (value, formFields) => {
                                if (formFields.weeklyLimit && !isNil(formFields.weeklyLimit)) {
                                    if ((value?.toString()?.length === 0 || value == null) && formFields.digitalWeeklyLimit) {
                                        return "Product Limit is required. Please enter a valid value to continue";
                                    }
                                }

                                return true;
                            },
                        },
                    },
                },
                {
                    name: "physicalMonthlyLimit",
                    rules: {
                        min: {
                            value: 0,
                            message: "Limit must be greater or equal to 0",
                        },
                        validate: {
                            required: (value, formFields) => {
                                if (formFields.monthlyLimit && !isNil(formFields.monthlyLimit)) {
                                    if ((value?.toString()?.length === 0 || value == null) && formFields.digitalMonthlyLimit) {
                                        return "Product Limit is required. Please enter a valid value to continue";
                                    }
                                }

                                if (value && formFields.physicalWeeklyLimit) {
                                    handleMonthlyLimitValidation(value, formFields.physicalWeeklyLimit, "physicalMonthlyLimit");
                                } else {
                                    warningCleanup("physicalMonthlyLimit");
                                }

                                return true;
                            },
                        },
                    },
                },
                {
                    name: "physicalAnnualLimit",
                    rules: {
                        min: {
                            value: 0,
                            message: "Limit must be greater or equal to 0",
                        },
                        validate: {
                            required: (value, formFields) => {
                                if (formFields.annualLimit && !isNil(formFields.annualLimit)) {
                                    if ((value?.toString()?.length === 0 || value == null) && formFields.digitalAnnualLimit) {
                                        return "Product Limit is required. Please enter a valid value to continue";
                                    }
                                }

                                handleAnnualLimitValidation(
                                    value,
                                    formFields.physicalWeeklyLimit,
                                    formFields.physicalMonthlyLimit,
                                    formFields.physicalAnnualLimit,
                                    "physicalAnnualLimit"
                                );

                                return true;
                            },
                        },
                    },
                },
            ],
            [
                {
                    name: "rowDescription",
                    label: "Digital:",
                },
                {
                    name: "digitalWeeklyLimit",
                    rules: {
                        min: {
                            value: 0,
                            message: "Limit must be greater or equal to 0",
                        },
                        validate: {
                            required: (value, formFields) => {
                                if (formFields.weeklyLimit && !isNil(formFields.weeklyLimit)) {
                                    if ((value?.toString()?.length === 0 || value == null) && formFields.physicalWeeklyLimit) {
                                        return "Product Limit is required. Please enter a valid value to continue";
                                    }
                                }

                                return true;
                            },
                        },
                    },
                },
                {
                    name: "digitalMonthlyLimit",
                    rules: {
                        min: {
                            value: 0,
                            message: "Limit must be greater or equal to 0",
                        },
                        validate: {
                            required: (value, formFields) => {
                                if (formFields.monthlyLimit && !isNil(formFields.monthlyLimit)) {
                                    if ((value?.toString()?.length === 0 || value == null) && formFields.physicalMonthlyLimit) {
                                        return "Product Limit is required. Please enter a valid value to continue";
                                    }
                                }

                                if (value && formFields.digitalWeeklyLimit) {
                                    handleMonthlyLimitValidation(value, formFields.digitalWeeklyLimit, "digitalMonthlyLimit");
                                } else {
                                    warningCleanup("digitalMonthlyLimit");
                                }

                                return true;
                            },
                        },
                    },
                },
                {
                    name: "digitalAnnualLimit",
                    rules: {
                        min: {
                            value: 0,
                            message: "Limit must be greater or equal to 0",
                        },
                        validate: {
                            required: (value, formFields) => {
                                if (formFields.annualLimit && !isNil(formFields.annualLimit)) {
                                    if ((value?.toString()?.length === 0 || value == null) && formFields.physicalAnnualLimit) {
                                        return "Product Limit is required. Please enter a valid value to continue";
                                    }
                                }

                                return true;
                            },
                        },
                    },
                },
            ],
        ];
    }, [handleAnnualLimitValidation, handleMonthlyLimitValidation, handleTotalValidation, warningCleanup]);
};
